import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import { Send, X, Play, CheckCircle2, AlertCircle } from "lucide-react";
import facebookIcon from "../assets/icons/facebookicon.webp";
import instagramIcon from "../assets/icons/igicon.webp";
import linkedinIcon from "../assets/icons/linkedinicon.webp";
import tiktokIcon from "../assets/icons/tiktokicon.webp";
import threadIcon from "../assets/icons/threadicon.webp";
import topLogo from "../assets/icons/top_logo.webp";
import img234 from "../assets/icons/234.webp";
import { API_BASE_URL } from "../lib/config";

const socialCircles = [
  { 
    name: "Threads", 
    icon: threadIcon, 
    size: "w-32 h-32 sm:w-48 sm:h-48", 
    pos: "top-[18%] right-[2%]", 
    z: "z-10", 
    rotation: -14,
    delay: 0.1,
    href: "https://www.threads.net/@wesitex",
  },
  { 
    name: "Facebook", 
    icon: facebookIcon, 
    size: "w-36 h-36 sm:w-56 sm:h-56", 
    pos: "top-[26%] left-[2%]", 
    z: "z-30", 
    rotation: 12,
    delay: 0.2,
    href: "https://www.facebook.com/wesitexsoft",
  },
  { 
    name: "Instagram", 
    icon: instagramIcon, 
    size: "w-32 h-32 sm:w-48 sm:h-48", 
    pos: "top-[2%] left-[24%]", 
    z: "z-20", 
    rotation: -6,
    delay: 0.3,
    href: "https://www.instagram.com/wesitex/",
  },
  { 
    name: "TikTok", 
    icon: tiktokIcon, 
    size: "w-32 h-32 sm:w-48 sm:h-48", 
    pos: "bottom-[16%] right-[4%]", 
    z: "z-40", 
    rotation: 16,
    delay: 0.4,
    href: "https://www.tiktok.com/@wesitex",
  },
  { 
    name: "LinkedIn", 
    icon: linkedinIcon, 
    size: "w-36 h-36 sm:w-56 sm:h-56", 
    pos: "bottom-[2%] left-[10%]", 
    z: "z-25", 
    rotation: -8,
    delay: 0.5,
    href: "https://www.linkedin.com/company/wesitex",
  },
];

export default function SocialMediaPage() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    title: string;
    message: string;
  } | null>(null);

  const triggerAlert = (type: "success" | "error", title: string, message: string) => {
    setAlert({ type, title, message });
    setTimeout(() => {
      setAlert(prev => {
        if (prev && prev.title === title && prev.message === message) {
          return null;
        }
        return prev;
      });
    }, 4500);
  };

  const handleSend = async () => {
    const msgText = message.trim();
    if (!msgText) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: msgText }),
      });
      if (response.ok) {
        triggerAlert(
          "success",
          "Message Sent!",
          "Thank you! Your message has been safely recorded in our database."
        );
      } else {
        triggerAlert(
          "error",
          "Submission Failed",
          "We were unable to save your message. Please try again."
        );
      }
    } catch (err) {
      console.error("Failed to save message to DB:", err);
      triggerAlert(
        "error",
        "Connection Error",
        "The system is currently unreachable. Please check your internet connection."
      );
    }

    setMessage("");
    setIsChatOpen(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-6 md:px-12 lg:px-24 flex items-center justify-center bg-transparent overflow-hidden selection:bg-blue-500/30">
      <div className="w-full max-w-7xl flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
        
        {/* Left Side Content */}
        <div className="flex-1 flex flex-col items-start w-full z-10 lg:pl-10">
          {/* Logo / Brand */}
          <div className="mb-12">
             <img src={topLogo} alt="Wesitex" className="h-28 w-auto object-contain" />
          </div>

          {/* Heading */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="font-black text-5xl sm:text-7xl lg:text-[5.5rem] text-white leading-[1.05] tracking-tight mb-8"
          >
            <div className="flex items-center gap-4 sm:gap-6">
              Our 
              <button 
                onClick={() => setIsModalOpen(true)}
                className="group relative flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-2xl sm:rounded-[2rem] bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 shadow-xl shadow-pink-500/30 hover:scale-105 hover:-rotate-3 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Play className="w-5 h-5 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-white fill-white ml-1" />
              </button>
            </div>
            Media Page <br />
            Fun fact !
          </motion.div>

          {/* Pill Button / Chat Input Container */}
          <div className="flex flex-col items-start relative z-20 w-full max-w-sm mt-4 gap-2">
            <div className="h-14 flex items-center w-full">
              <AnimatePresence mode="wait">
                {!isChatOpen ? (
                  <motion.button
                    key="button"
                    onClick={() => setIsChatOpen(true)}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.5, delay: 0.1, ease: "backOut" }}
                    className="inline-flex items-center justify-center px-8 py-3 bg-[#4F65F1] text-white font-bold rounded-full text-sm sm:text-base hover:bg-blue-600 transition-colors cursor-pointer shadow-lg shadow-blue-500/25 tracking-wide w-auto"
                  >
                    Leave a Message for Us! 
                  </motion.button>
                ) : (
                  <motion.div
                    key="chat"
                    initial={{ opacity: 0, width: 40 }}
                    animate={{ opacity: 1, width: "100%" }}
                    exit={{ opacity: 0, width: 40 }}
                    transition={{ duration: 0.4, type: "spring", stiffness: 200, damping: 20 }}
                    className="relative flex items-center w-full bg-slate-950/80 border border-white/15 rounded-full p-1 shadow-2xl backdrop-blur-md overflow-hidden"
                  >
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 bg-transparent px-4 py-2 outline-none text-white placeholder-slate-500 text-sm font-medium w-full"
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      autoFocus
                    />
                    <div className="flex items-center gap-1 pr-1 shrink-0">
                      <button
                        onClick={() => setIsChatOpen(false)}
                        className="p-1.5 text-slate-400 hover:text-white transition-colors rounded-full"
                      >
                        <X size={18} />
                      </button>
                      <button
                        onClick={handleSend}
                        className="flex items-center justify-center w-9 h-9 bg-[#4F65F1] hover:bg-blue-600 text-white rounded-full transition-colors"
                      >
                        <Send size={16} className="-ml-0.5" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Anonymous message note */}
            <p className="text-xs text-slate-400 font-mono pl-4 animate-fadeIn">
              🤫 it's okay, no one is gonna know who u are
            </p>
          </div>
        </div>

        {/* Right Side Graphic */}
        <div className="flex-1 w-full flex items-center justify-center shrink-0 min-h-[480px] sm:min-h-[600px] z-10">
          <div className="relative w-[320px] h-[450px] sm:w-[460px] sm:h-[580px]">
            {socialCircles.map((social) => (
              <motion.a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0, rotate: social.rotation }}
                animate={{ opacity: 0.95, scale: 1, rotate: social.rotation }}
                whileHover={{ scale: 1.1, rotate: 0, opacity: 1, zIndex: 60 }}
                transition={{ 
                  delay: social.delay,
                  type: "spring",
                  stiffness: 260,
                  damping: 20
                }}
                className={`group absolute ${social.pos} ${social.size} ${social.z} flex items-center justify-center cursor-pointer hover:-translate-y-4 transition-transform duration-300 filter drop-shadow-[0_15px_25px_rgba(0,0,0,0.45)]`}
              >
                {/* Tooltip / Speech Bubble */}
                <div className="absolute -top-20 left-1/2 -translate-x-1/2 opacity-0 scale-50 group-hover:scale-100 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50 origin-bottom">
                  <div className="relative filter drop-shadow-[4px_4px_0px_rgba(0,0,0,1)]">
                    <div className="bg-white border-[3px] border-black rounded-2xl px-5 py-2 font-black text-slate-800 text-sm sm:text-base whitespace-nowrap relative z-10">
                      @wesitex
                    </div>
                    <div className="absolute -bottom-2 left-6 w-5 h-5 bg-white border-b-[3px] border-r-[3px] border-black rotate-45 z-0"></div>
                  </div>
                </div>

                {/* Logo Image */}
                <div 
                  className="w-full h-full flex items-center justify-center"
                >
                  <img 
                    src={social.icon} 
                    alt={social.name} 
                    className="w-full h-full object-contain" 
                  />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
        
      </div>

      {/* Content Modal (Reels Popup) */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm aspect-[9/16] bg-slate-900 rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-slate-800 flex flex-col items-center justify-center"
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-md"
              >
                <X size={20} />
              </button>
              
              {/* Content (234.webp image) */}
              <img 
                src={img234} 
                alt="Wesitex Reels" 
                className="w-full h-full object-cover" 
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Popup Alert */}
      <AnimatePresence>
        {alert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0, transition: { type: "spring", damping: 15 } }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm bg-slate-900/95 border border-white/10 rounded-3xl p-6 md:p-8 shadow-[0_0_50px_rgba(139,92,246,0.3)] text-center overflow-hidden"
            >
              {/* Decorative radial gradient */}
              <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl" />
              <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-blue-500/20 blur-3xl" />
              
              <button
                onClick={() => setAlert(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="flex flex-col items-center">
                {alert.type === "success" ? (
                  <div className="w-16 h-16 rounded-2xl bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-6 text-green-400 shadow-[0_0_20px_rgba(34,197,94,0.2)] animate-pulse">
                    <CheckCircle2 size={36} />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-6 text-red-400 shadow-[0_0_20px_rgba(239,68,68,0.2)] animate-pulse">
                    <AlertCircle size={36} />
                  </div>
                )}

                <h3 className="font-orbitron font-bold text-lg md:text-xl text-white tracking-wide mb-2 uppercase">
                  {alert.title}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed font-sans">
                  {alert.message}
                </p>

                <button
                  onClick={() => setAlert(null)}
                  className="mt-6 px-6 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-xs font-mono font-bold tracking-wider rounded-xl transition-all shadow-lg active:scale-95 cursor-pointer uppercase"
                >
                  Dismiss
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
